import React, { useState, useEffect, useCallback } from "react";
import { Paper, Typography, Button, Chip, CircularProgress, Divider } from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ReminderStepTemplateSection from "../../components/ReminderStepTemplateSection";

// ─── Plantilla por recordatorio (R-0893) ──────────────────────────────────────
// Qué plantilla Meta usa cada paso de recordatorio de la empresa, editable desde
// el panel superadmin (antes sólo desde /turnos → Ajustes → editar paso, con un
// usuario admin de la empresa). Usa los endpoints superadmin de TurnosIA vía el
// proxy de WAIA; la validación real la hace TurnosIA al guardar.

const CATEGORY_COLORS = { UTILITY: "#2e7d32", MARKETING: "#ed6c02", AUTHENTICATION: "#1565c0" };

const toDraft = (mt) =>
  mt?.name ? { name: mt.name, language: mt.language, variables: [...(mt.variables || [])] } : null;

const sameDraft = (a, b) => JSON.stringify(a || null) === JSON.stringify(b || null);

const categoryLabel = (c) =>
  i18n.exists(`reminderStepTemplate.category.${c}`) ? i18n.t(`reminderStepTemplate.category.${c}`) : c;

const ReminderTemplatesSection = ({ company }) => {
  const [steps, setSteps] = useState([]);
  const [options, setOptions] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    setLoadError(false);
    try {
      const [cfg, tpl] = await Promise.all([
        api.get(`/superadmin/turnos/companies/${company.id}/reminder-config`),
        api.get(`/superadmin/turnos/companies/${company.id}/reminder-templates`),
      ]);
      const list = Array.isArray(cfg.data) ? [...cfg.data].sort((a, b) => a.stepOrder - b.stepOrder) : [];
      setSteps(list);
      setOptions(tpl.data || null);
      const d = {};
      list.forEach((s) => { d[s.id] = toDraft(s.metaTemplate); });
      setDrafts(d);
    } catch (e) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  useEffect(() => { load(); }, [load]);

  const findTemplate = (draft) =>
    draft?.name ? (options?.templates || []).find((t) => t.name === draft.name && t.language === draft.language) : null;

  const saveStep = async (step, draft) => {
    const { data } = await api.patch(
      `/superadmin/turnos/companies/${company.id}/reminder-config/${step.id}`,
      { metaTemplate: draft }
    );
    return data;
  };

  const handleSave = async (step) => {
    const draft = drafts[step.id];
    if (draft && (draft.variables || []).some((v) => !v)) {
      toast.error(i18n.t("reminderStepTemplate.errorVarsIncompletas"));
      return;
    }
    setSaving((p) => ({ ...p, [step.id]: true }));
    try {
      const data = await saveStep(step, draft);
      setSteps((prev) => prev.map((s) => (s.id === step.id ? { ...s, metaTemplate: data?.metaTemplate ?? null } : s)));
      toast.success(i18n.t("reminderStepTemplate.superadmin.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.error || i18n.t("reminderStepTemplate.errorGuardar"));
    } finally {
      setSaving((p) => ({ ...p, [step.id]: false }));
    }
  };

  const handleApplyAll = async (source) => {
    const draft = drafts[source.id];
    if (draft && (draft.variables || []).some((v) => !v)) {
      toast.error(i18n.t("reminderStepTemplate.errorVarsIncompletas"));
      return;
    }
    setSaving((p) => ({ ...p, [source.id]: true }));
    try {
      const updated = {};
      for (const s of steps) {
        const data = await saveStep(s, draft);
        updated[s.id] = data?.metaTemplate ?? null;
      }
      setSteps((prev) => prev.map((s) => ({ ...s, metaTemplate: updated[s.id] })));
      setDrafts((prev) => {
        const n = { ...prev };
        steps.forEach((s) => { n[s.id] = toDraft(updated[s.id]); });
        return n;
      });
      toast.success(i18n.t("reminderStepTemplate.superadmin.appliedAll"));
    } catch (e) {
      toast.error(e?.response?.data?.error || i18n.t("reminderStepTemplate.errorGuardar"));
      load();
    } finally {
      setSaving((p) => ({ ...p, [source.id]: false }));
    }
  };

  return (
    <>
      <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4, marginTop: 4 }}>
        {i18n.t("reminderStepTemplate.superadmin.title")}
      </Typography>
      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
        {i18n.t("reminderStepTemplate.superadmin.help")}
      </Typography>
      <Typography variant="caption" style={{ display: "block", marginBottom: 8, color: "#0b5394" }}>
        {i18n.t("reminderStepTemplate.superadmin.overridesNote")}
      </Typography>
      <Paper variant="outlined" style={{ padding: "14px 16px", marginBottom: 16, borderRadius: 8 }}>
        {loading ? (
          <CircularProgress size={20} />
        ) : loadError ? (
          <Typography variant="caption" color="error">{i18n.t("reminderStepTemplate.superadmin.loadError")}</Typography>
        ) : steps.length === 0 ? (
          <Typography variant="caption" color="textSecondary">{i18n.t("reminderStepTemplate.superadmin.noSteps")}</Typography>
        ) : (
          steps.map((step, i) => {
            const draft = drafts[step.id] || null;
            const current = step.metaTemplate;
            const currentTpl = findTemplate(current);
            const draftTpl = findTemplate(draft);
            const dirty = !sameDraft(draft, toDraft(current));
            const category = currentTpl?.category || current?.category;
            return (
              <div key={step.id}>
                {i > 0 && <Divider style={{ margin: "12px 0" }} />}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {i18n.t("reminderStepTemplate.superadmin.step", { n: step.stepOrder, hours: step.hoursBeforeAppointment })}
                  </Typography>
                  {!step.enabled && (
                    <Chip size="small" variant="outlined" label={i18n.t("reminderStepTemplate.superadmin.inactive")} style={{ height: 20 }} />
                  )}
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("reminderStepTemplate.superadmin.current", {
                      name: current?.name || i18n.t("reminderStepTemplate.automatic"),
                    })}
                  </Typography>
                  {current?.name && category && (
                    <Chip
                      size="small"
                      label={categoryLabel(category)}
                      style={{ height: 20, backgroundColor: CATEGORY_COLORS[category] || "#757575", color: "#fff" }}
                    />
                  )}
                </div>
                <ReminderStepTemplateSection
                  value={draft}
                  onChange={(v) => setDrafts((p) => ({ ...p, [step.id]: v }))}
                  options={options}
                  loading={false}
                />
                {draftTpl?.category === "MARKETING" && (
                  <Typography variant="caption" style={{ display: "block", color: "#ed6c02", marginTop: 4 }}>
                    {i18n.t("reminderStepTemplate.superadmin.marketing")}
                  </Typography>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={!dirty || !!saving[step.id]}
                    onClick={() => handleSave(step)}
                  >
                    {saving[step.id] ? <CircularProgress size={16} /> : i18n.t("reminderStepTemplate.superadmin.save")}
                  </Button>
                  {steps.length > 1 && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!!saving[step.id]}
                      onClick={() => handleApplyAll(step)}
                    >
                      {i18n.t("reminderStepTemplate.superadmin.applyAll")}
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </Paper>
    </>
  );
};

export default ReminderTemplatesSection;
